import { Component, Output, Input, EventEmitter, OnInit } from '@angular/core';

import { TargetingResultModel } from '../../../../../models/targeting-result.model';
import { TargetingSearchService } from '../../../../../services/targeting-search.service';
import { LocalizationService } from '../../../../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../../../../Core/Dialogs/services/dialog.service';
import { TargetingListService } from '../../../../../services/targeting-list.service';
import { ListPickerService } from '../../../../../../../Common/RhpContexts/services/list-picker.service';
import { ResultCardDirective } from '../../../../../../../Common/TargetingShared/components/ResultCard/result-card.directive';
import { DistributionReportStat } from '../../../../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { RecipientResultModel } from '../../../../../models/recipient.result.model';
import { EmailEventApiModel } from '../../../../../../../api/distributions/model/email-event.api.model';
import { ContactService } from '../../../../../../../Common/TargetingShared/services/contact-service';
import { EntityType } from '../../../../../../../api/targeting/enum/entity.type.enum';
import { HandleTypeEnum } from '../../../../../../../api/targeting/enum/handle-type.enum';
import { TargetingRhpContextsService } from '../../../../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { EntityTypeDefaultImageEnum } from '../../../../../../../Common/Misc/enums/entity-type-default-image.enum';
import { topStatLabels } from '../../../../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-map.model';
import { ManageListDialogComponent } from '../../../../TargetingResults/ManageListDialog/manage-list-dialog.component';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { filter } from 'rxjs';
import { DistributionPickerService } from '@Common/RhpContexts/services/distribution-picker.service';
import { ManageDistDialogComponent } from '@Discover/Targeting/components/TargetingResults/ManageDistDialog/manage-dist-dialog.component';
import { DataConnectionService } from '../../../../../../../Core/Users/services/data-connection.service';

export interface RecipientRhpTakeoverEvent {
    entity: TargetingResultModel;
    selectedEntityType: EntityType;
}
@Component({
    selector: 'agpr-recipient-card',
    templateUrl: './recipient-card.component.html',
    styleUrls: ['../../../../../../../Common/TargetingShared/components/ResultCard/result-card.scss', './recipient-card.component.scss'],
    standalone: false
})

export class RecipientCardComponent extends ResultCardDirective implements OnInit {
    readonly topStatLabels = topStatLabels;
    readonly statTypes = DistributionReportStat;
    readonly handleTypes = HandleTypeEnum;
    readonly entityTypes = EntityType;
    readonly imageDefault = EntityTypeDefaultImageEnum;

    @Input() id: string;
    @Input() isPrimaryContact: boolean = false;
    @Input() set entity(value: RecipientResultModel) {
        this.entityInternal = value;
        this.setEmail();
        this.linkEvents = value.events?.filter(x => x.type === DistributionReportStat.UNIQUE_CLICKS);
        this.otherEvents = value.events?.filter(x => x.type !== DistributionReportStat.UNIQUE_CLICKS);
        this.updateListMembershipInfo();
    }

    @Output() showRhpTakeOver?: EventEmitter<RecipientRhpTakeoverEvent> = new EventEmitter<RecipientRhpTakeoverEvent>();
    @Output() composeEmail: EventEmitter<string> = new EventEmitter<string>();
    @Output() viewConversation: EventEmitter<string> = new EventEmitter<string>();

    declare entityInternal: RecipientResultModel;
    linkEvents: EmailEventApiModel[];
    otherEvents: EmailEventApiModel[];
    eventTimeZone: string = 'UTC';

    constructor(
        public targetingSearchService: TargetingSearchService,
        public targetingListService: TargetingListService,
        public listPickerService: ListPickerService,
        public localizationService: LocalizationService,
        public contactService: ContactService,
        public coreDialogService: CoreDialogService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public dataConnectionService: DataConnectionService,
        public distributionPickerService: DistributionPickerService,
        private currentUserStateService: CurrentUserStateService
    ) {
        super(targetingSearchService,
            targetingListService,
            listPickerService,
            localizationService,
            coreDialogService,
            targetingRhpContextsService,
            distributionPickerService);
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(
                filter(x => !!x),
            ).subscribe(preferences => {
                this.eventTimeZone = preferences?.timeZone ?? 'UTC';
            })
        );
    }

    onCardClicked(entity: TargetingResultModel) {
        this.showRhpTakeOver.emit({
            entity: entity,
            selectedEntityType: entity.entityType
        });
    }

    focusOutlet(entity: TargetingResultModel) {
        this.showRhpTakeOver.emit({
            entity: entity,
            selectedEntityType: EntityType.OUTLET
        });
    }

    /**
     * does 2 different checks to add the distributedEmail to the recipient card, which displays the email in the recipient card
     */
    setEmail() {
        // if not distributedEmail, check contactModel primary email
        if (!this.entityInternal?.distributedEmail) {
            if (this.entityInternal.contactModel?.primaryEmail) {
                this.entityInternal.distributedEmail = this.entityInternal.contactModel?.primaryEmail;
                return;
            }
        }

        // if still not distributedEmail, check handles for type email
        if (!this.entityInternal?.distributedEmail) {
            if (this.entityInternal.handles) {
                for (const handle of this.entityInternal.handles) {
                    if (handle.handleType === HandleTypeEnum.EMAIL) {
                        this.entityInternal.distributedEmail = handle.value;
                        break;
                    }
                }
            }
        }
    }

    openManageListDialog(entity: TargetingResultModel) {
        this.coreDialogService.open(ManageListDialogComponent, {
            inputs: {
                setEntity: entity
            },
            outputs: {}
        });
    }

    openManageDistDialog(entity: TargetingResultModel) {
        this.coreDialogService.open(ManageDistDialogComponent, {
            inputs: {
                setEntity: entity
            },
            outputs: {}
        });
    }
}
